import { useMemo } from "react"
import PropTypes from "prop-types"
import { reduce, map, find, keys, omitBy } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { UUID, ROLE, DATASET_TOKEN } from "helpers/shapes"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function DatasetTokens({ account, role, datasetId, tokens, datasetTokens, update, tabs }) {
  const hasWriteAccess = role === "admin"

  const optionLabels = useMemo(() => reduce(tokens, (tokensById, token) => ({
    ...tokensById,
    [token.id]: token.name,
  }), {}),
  [tokens])

  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(datasetTokens, token => token.id === id)),
  [datasetTokens, optionLabels])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($datasetId: String!, $tokenIds: [Id!]){
          grantDatasetAccess(id: $datasetId, tokenIds: $tokenIds) { id }
      }`

      const variables = {
        datasetId,
        tokenIds: data.tokens,
      }
      update(query, variables, account, {
        reloadData: ["datasetEntitlements", "tokenDatasetEntitlements", "userDatasets", "models"],
      }, cb)
    }

    return {
      title: "Grant Dataset Access",
      submitText: "Grant access",
      track: {
        type: "dataset-entitlements-grant-dataset-tokens",
        payload: ({ tokens }) => ({
          tokens,
          datasetId,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "tokens",
        label: "Tokens",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, datasetId, update, account])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($datasetId: String!, $tokenIds: [Id!]){
        revokeDatasetAccess(id: $datasetId, tokenIds: $tokenIds) { id }
    }`
      const variables = {
        datasetId,
        tokenIds: map(data, "id"),
      }

      update(query, variables, account, {
        reloadData: ["datasetEntitlements", "tokenDatasetEntitlements", "userDatasets", "models"],
      }, cb)
    }

    return {
      type: "dataset-entitlement",
      track: {
        type: "dataset-entitlements-revoke-dataset-tokens",
        payload: (token) => ({
          token: token.id,
          datasetId,
        }),
      },
      // Only direct entitlements can be removed
      isEnabled: () => hasWriteAccess,
      renderItem: dataset => <li key={dataset.id}>{dataset.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, datasetId, update])

  return <div className="modules_dataset-entitlements-tokens">
    {role === "admin" && <UpsellTabs id="dataset-tokens" tabs={tabs} />}
    <Paper className="content" square>
      <Table
        title="Token entitlements"
        idField="id"
        addForm={addForm}
        editForm={null}
        deleteForm={deleteForm}
        data={datasetTokens}
        columns={[{
          name: "Name",
          id: "name",
          linkTo: (token) => role === "admin" && `/${account}/home/tokens/${token.id}/entitlements`,
        }]}
      />
    </Paper>
  </div>
}

DatasetTokens.propTypes = {
  account: PropTypes.string.isRequired,
  datasetTokens: PropTypes.arrayOf(DATASET_TOKEN).isRequired,
  datasetId: PropTypes.string.isRequired,
  userId: UUID,
  role: ROLE.isRequired,
  tokens: PropTypes.array,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
